import { InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import * as portalSession from '../auth/portalSession';

class TokenManager {
  constructor() {
    this.refreshTimer = null;
    this.tokenExpiryTime = null;
    this.isRefreshing = false;
    this.refreshCallbacks = [];
    this.isHandlingAuthError = false;
    this.failedRefreshCount = 0;
    this.maxFailedRefreshAttempts = 3;
    this.lastRefreshAttempt = null;
  }

  // Wait for MSAL to be initialized before making any token requests
  async ensureMsalInitialized() {
    // Dynamically import to avoid circular dependency
    const { waitForMsalInit } = await import('../App');
    if (waitForMsalInit) {
      await waitForMsalInit();
    }
  }

  // Get token with proactive refresh
  async getToken(instance, accounts, retryCount = 0) {
    // External user signed in with an e-mail code: no MSAL account, portal token.
    if (portalSession.isActive()) {
      const t = await portalSession.getAccessToken();
      if (t) return t;
      this.forceRelogin();
      return null;
    }
    try {
      // Ensure MSAL is fully initialized before attempting token acquisition
      await this.ensureMsalInitialized();
      
      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }

      // Check if we need to refresh the token
      if (this.shouldRefreshToken()) {
        console.log('Proactively refreshing token before expiry...');
        return await this.refreshToken(instance, accounts);
      }

      // Get fresh token
      const response = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0],
        forceRefresh: false // Don't force refresh on first attempt
      });

      // Check if the returned token is already expired
      const now = new Date();
      const tokenExpiry = new Date(response.expiresOn);
      
      if (tokenExpiry <= now) {
        console.log('Received expired token from cache, forcing refresh...');
        return await this.refreshToken(instance, accounts);
      }

      // Set up next refresh (5 minutes before expiry)
      this.scheduleTokenRefresh(response.expiresOn, instance, accounts);
      
      // Reset failure count on success
      this.failedRefreshCount = 0;
      
      return response.idToken;
    } catch (error) {
      console.error('Error acquiring token:', error);
      
      // For timeout errors, try popup-based acquisition as fallback
      const isTimeoutError = error.errorCode === 'monitor_window_timeout' || 
                            (error.message && error.message.includes('monitor_window_timeout'));
      
      if (isTimeoutError && retryCount < 1) {
        console.log('Iframe token acquisition timed out, trying popup fallback...');
        try {
          // Try acquiring token via popup instead of iframe
          const popupResponse = await instance.acquireTokenPopup({
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0]
          });
          
          if (popupResponse && popupResponse.idToken) {
            console.log('Successfully acquired token via popup');
            this.scheduleTokenRefresh(popupResponse.expiresOn, instance, accounts);
            this.failedRefreshCount = 0;
            return popupResponse.idToken;
          }
        } catch (popupError) {
          console.error('Popup token acquisition also failed:', popupError);
          // Continue to normal error handling
        }
      }
      
      // Check for errors that require re-authentication
      const requiresReauth = this.isReauthRequiredError(error);
      
      if (requiresReauth) {
        // Token expired and can't be renewed silently, redirect to login
        if (!this.isHandlingAuthError) {
          this.isHandlingAuthError = true;
          console.log('Token expired and cannot be renewed silently, redirecting to login...');
          this.forceRelogin();
        }
        return null; // This won't be reached due to redirect, but good for type safety
      }
      
      throw error;
    }
  }

  // Check if we should refresh the token (5 minutes before expiry)
  shouldRefreshToken() {
    if (!this.tokenExpiryTime) {
      console.log('No token expiry time set, no refresh needed');
      return false;
    }
    
    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutes
    
    const shouldRefresh = this.tokenExpiryTime <= fiveMinutesFromNow;
    
    if (shouldRefresh) {
      console.log(`Token should be refreshed. Expiry: ${this.tokenExpiryTime}, Current time: ${now}`);
    }
    
    return shouldRefresh;
  }

  // Schedule token refresh
  scheduleTokenRefresh(expiresOn, instance, accounts) {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Calculate when to refresh (5 minutes before expiry)
    const expiryTime = new Date(expiresOn);
    const refreshTime = new Date(expiryTime.getTime() - 5 * 60 * 1000); // 5 minutes before
    const now = new Date();
    
    // If we're already past the refresh time, refresh immediately
    if (refreshTime <= now) {
      console.log('Token refresh time has passed, will refresh on next request');
      this.tokenExpiryTime = expiryTime;
      return;
    }

    // Schedule refresh
    const timeUntilRefresh = refreshTime.getTime() - now.getTime();
    this.tokenExpiryTime = expiryTime;
    
    console.log(`Scheduling token refresh in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
    
    this.refreshTimer = setTimeout(async () => {
      console.log('Scheduled token refresh triggered - proactively refreshing token');
      const maxRetries = 3;
      let attempt = 0;

      while (attempt < maxRetries) {
        try {
          await this.refreshToken(instance, accounts);
          console.log('Proactive token refresh completed successfully');
          return;
        } catch (error) {
          attempt += 1;
          console.error(`Proactive token refresh failed (attempt ${attempt}/${maxRetries}):`, error);

          if (attempt < maxRetries) {
            const backoffMs = 1000 * Math.pow(2, attempt - 1);
            await new Promise(resolve => setTimeout(resolve, backoffMs));
          } else {
            // Clear token expiry so next request will attempt refresh
            this.tokenExpiryTime = null;
          }
        }
      }
    }, timeUntilRefresh);
  }

  // Refresh token
  async refreshToken(instance, accounts) {
    // If already refreshing, wait for it to complete
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshCallbacks.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      console.log('Refreshing token...');
      
      const response = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0],
        forceRefresh: true // Force refresh to get a new token
      });

      // Double-check the new token isn't expired
      const now = new Date();
      const tokenExpiry = new Date(response.expiresOn);
      
      if (tokenExpiry <= now) {
        throw new Error('Received expired token even after forced refresh');
      }

      // Schedule next refresh
      this.scheduleTokenRefresh(response.expiresOn, instance, accounts);
      
      // Resolve all waiting callbacks
      this.refreshCallbacks.forEach(callback => callback.resolve(response.idToken));
      this.refreshCallbacks = [];
      
      this.isRefreshing = false;
      return response.idToken;
    } catch (error) {
      console.error('Error refreshing token:', error);
      
      // For timeout errors, try popup as fallback
      const isTimeoutError = error.errorCode === 'monitor_window_timeout' || 
                            (error.message && error.message.includes('monitor_window_timeout'));
      
      if (isTimeoutError) {
        console.log('Silent refresh timed out, trying popup fallback...');
        try {
          const popupResponse = await instance.acquireTokenPopup({
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0]
          });
          
          if (popupResponse && popupResponse.idToken) {
            console.log('Successfully refreshed token via popup');
            this.scheduleTokenRefresh(popupResponse.expiresOn, instance, accounts);
            
            // Resolve all waiting callbacks
            this.refreshCallbacks.forEach(callback => callback.resolve(popupResponse.idToken));
            this.refreshCallbacks = [];
            
            this.isRefreshing = false;
            return popupResponse.idToken;
          }
        } catch (popupError) {
          console.error('Popup refresh also failed:', popupError);
          // Continue to normal error handling
        }
      }
      
      // Reject all waiting callbacks
      this.refreshCallbacks.forEach(callback => callback.reject(error));
      this.refreshCallbacks = [];
      
      this.isRefreshing = false;
      
      // Check for errors that require re-authentication
      const requiresReauth = this.isReauthRequiredError(error);
      
      if (requiresReauth) {
        if (!this.isHandlingAuthError) {
          this.isHandlingAuthError = true;
          console.log('Token refresh failed and cannot be renewed silently, redirecting to login...');
          this.forceRelogin();
        }
        return null; // This won't be reached due to redirect, but good for type safety
      }
      
      throw error;
    }
  }

  // Handle 401 authentication error (called by apiClient)
  // Returns true if caller should retry, false if giving up
  async handle401Error(instance, accounts) {
    if (portalSession.isActive()) {
      const t = await portalSession.refresh();
      if (t) return true;
      this.forceRelogin();
      return false;
    }
    // Prevent rapid-fire 401 handling from multiple components
    const now = Date.now();
    if (this.lastRefreshAttempt && (now - this.lastRefreshAttempt) < 1000) {
      console.log('Throttling 401 handling - too many requests');
      // Wait for the ongoing refresh to complete
      if (this.isRefreshing) {
        return new Promise((resolve, reject) => {
          this.refreshCallbacks.push({ 
            resolve: () => resolve(true), 
            reject: () => resolve(false) 
          });
        });
      }
      return false;
    }
    this.lastRefreshAttempt = now;

    // Check if we've exceeded max failed attempts
    if (this.failedRefreshCount >= this.maxFailedRefreshAttempts) {
      console.log(`Max failed refresh attempts (${this.maxFailedRefreshAttempts}) exceeded, forcing re-login`);
      this.forceRelogin();
      return false;
    }

    try {
      const newToken = await this.refreshToken(instance, accounts);
      if (newToken) {
        this.failedRefreshCount = 0; // Reset on success
        return true; // Caller should retry
      }
    } catch (error) {
      this.failedRefreshCount++;
      console.error(`Token refresh failed (attempt ${this.failedRefreshCount}/${this.maxFailedRefreshAttempts}):`, error);
      
      if (this.failedRefreshCount >= this.maxFailedRefreshAttempts) {
        this.forceRelogin();
      }
    }
    
    return false;
  }

  // Check if an error requires re-authentication
  isReauthRequiredError(error) {
    // InteractionRequiredAuthError - user interaction is needed
    if (error instanceof InteractionRequiredAuthError) {
      return true;
    }
    
    // BrowserAuthError - check for specific error codes
    if (error instanceof BrowserAuthError) {
      // These errors definitely require re-authentication
      const definiteReauthCodes = [
        'silent_sso_error', 
        'no_account_error',
        'user_cancelled',
        'consent_required',
        'login_required'
      ];
      
      if (definiteReauthCodes.includes(error.errorCode)) {
        console.log(`BrowserAuthError requiring reauth: ${error.errorCode}`);
        return true;
      }
      
      // monitor_window_timeout is often transient - only force reauth after multiple failures
      if (error.errorCode === 'monitor_window_timeout') {
        this.failedRefreshCount++;
        console.log(`BrowserAuthError: monitor_window_timeout (attempt ${this.failedRefreshCount}/${this.maxFailedRefreshAttempts})`);
        
        // Only force reauth after max attempts
        if (this.failedRefreshCount >= this.maxFailedRefreshAttempts) {
          console.log('Max timeout attempts reached, requiring reauth');
          return true;
        }
        
        // Otherwise, let the caller retry
        return false;
      }
    }
    
    // Check error message for timeout-related issues (fallback)
    if (error.message && error.message.includes('monitor_window_timeout')) {
      this.failedRefreshCount++;
      console.log(`Detected monitor_window_timeout from message (attempt ${this.failedRefreshCount}/${this.maxFailedRefreshAttempts})`);
      return this.failedRefreshCount >= this.maxFailedRefreshAttempts;
    }
    
    return false;
  }

  // Force user to re-login by clearing MSAL cache and redirecting
  forceRelogin() {
    if (!this.isHandlingAuthError) {
      this.isHandlingAuthError = true;
      console.log('Authentication failed, forcing re-login...');
      this.clear();
      portalSession.clear();
      
      // Clear MSAL's sessionStorage cache to ensure fresh authentication
      // MSAL stores auth data with keys starting with 'msal.'
      this.clearMsalCache();
      
      // Redirect to origin (home) to trigger fresh MSAL login flow
      window.location.href = window.location.origin;
    }
  }

  // Clear MSAL cache from sessionStorage
  clearMsalCache() {
    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        // MSAL cache keys typically start with 'msal.' or contain account/token info
        if (key && (key.startsWith('msal.') || key.includes('.idtoken') || 
            key.includes('.accesstoken') || key.includes('.refreshtoken') ||
            key.includes('login.windows.net') || key.includes('.account.'))) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
        console.log(`Cleared MSAL cache key: ${key}`);
      });
      
      console.log(`Cleared ${keysToRemove.length} MSAL cache entries from sessionStorage`);
    } catch (e) {
      console.error('Error clearing MSAL cache:', e);
      // As a fallback, try clearing all sessionStorage
      try {
        sessionStorage.clear();
        console.log('Cleared all sessionStorage as fallback');
      } catch (e2) {
        console.error('Error clearing sessionStorage:', e2);
      }
    }
  }

  // Clear timers and state
  clear() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.tokenExpiryTime = null;
    this.isRefreshing = false;
    this.refreshCallbacks = [];
    // Don't reset isHandlingAuthError here - it's intentionally kept to prevent loops
    this.failedRefreshCount = 0;
    this.lastRefreshAttempt = null;
  }

  // Full reset (only call when intentionally logging out)
  fullReset() {
    this.clear();
    this.isHandlingAuthError = false;
  }
}

// Create singleton instance
const tokenManager = new TokenManager();

export default tokenManager;
